import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FeatherIcon from "feather-icons-react";
import { RootState } from "../redux/store";
import mogaLogo from "../assets/images/moga/moga_dMB.png";

type Msg = { from: "bot" | "user"; text: string };

interface FaqNode {
  id: string;
  /** Quick-reply label shown as a button. */
  label: string;
  /** Bot answer when the topic is picked. */
  answer: string;
  /** Optional navigation target. */
  link?: { path: string; label: string };
  /** Roles allowed to see this topic; undefined = everyone. */
  roles?: string[];
}

const FAQ: FaqNode[] = [
  {
    id: "orders",
    label: "Suivre mes commandes",
    answer:
      "Tes commandes sont dans le menu « Orders ». Tu peux filtrer par statut (en attente, confirmée, annulée…) et ouvrir une commande pour voir son historique.",
    link: { path: "/orders/list", label: "Ouvrir les commandes" },
    roles: ["admin", "seller", "agent", "moderator", "supervisor"],
  },
  {
    id: "shipping",
    label: "Expéditions & livraisons",
    answer:
      "Le suivi des livraisons se trouve dans « Shippings ». Le statut passe de en attente → livré. La date de livraison est enregistrée automatiquement.",
    link: { path: "/shipping/list", label: "Ouvrir les expéditions" },
    roles: ["admin", "seller", "agent", "moderator", "supervisor", "delivery"],
  },
  {
    id: "stock",
    label: "Gérer le stock",
    answer:
      "La page « Stock » affiche les quantités restantes et les manques. Chaque mouvement de stock est tracé lors des confirmations et annulations.",
    link: { path: "/stock/list", label: "Ouvrir le stock" },
    roles: ["admin", "moderator", "supervisor"],
  },
  {
    id: "wallet",
    label: "Mon solde & retraits",
    answer:
      "Ton solde de commissions s'affiche dans la barre du haut. Pour demander un retrait, va dans ton profil : saisis le montant et envoie la demande. Un admin la validera.",
    link: { path: "/users/profile", label: "Ouvrir mon profil" },
    roles: ["agent"],
  },
  {
    id: "agent-commissions",
    label: "Commissions des agents",
    answer:
      "Dans « Agent Commissions » tu règles le montant par commande livrée de chaque agent, tu vois leur solde et tu valides ou rejettes leurs demandes de retrait.",
    link: { path: "/agent-commissions", label: "Ouvrir les commissions" },
    roles: ["admin"],
  },
  {
    id: "profile",
    label: "Modifier mon profil",
    answer:
      "Tu peux mettre à jour ton nom, ton téléphone et tes infos de paiement depuis ton profil.",
    link: { path: "/users/profile", label: "Ouvrir mon profil" },
  },
  {
    id: "support",
    label: "Contacter le support",
    answer:
      "Pour un problème ou une réclamation, ouvre un ticket dans « Support & Claims ». Notre équipe te répondra rapidement.",
    link: { path: "/support/list", label: "Ouvrir le support" },
    roles: ["admin", "seller", "moderator", "supervisor"],
  },
];

const GREETING =
  "Bonjour 👋 Je suis Moga, ton assistant. Choisis un sujet ci-dessous :";

const WHATSAPP_PHONE = (process.env.REACT_APP_WHATSAPP_PHONE || "").replace(
  /\D/g,
  ""
);
const WHATSAPP_URL = WHATSAPP_PHONE
  ? `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(
      "Bonjour 👋, j'ai besoin d'aide."
    )}`
  : "";

const MogaChatbot = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.Auth);
  const role = user?.role || "";

  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([
    { from: "bot", text: GREETING },
  ]);
  const bodyRef = useRef<HTMLDivElement>(null);

  const topics = useMemo(
    () => FAQ.filter((n) => !n.roles || n.roles.includes(role)),
    [role]
  );

  const [activeNode, setActiveNode] = useState<FaqNode | null>(null);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, open]);

  const pickTopic = (node: FaqNode) => {
    setMessages((m) => [
      ...m,
      { from: "user", text: node.label },
      { from: "bot", text: node.answer },
    ]);
    setActiveNode(node);
  };

  const backToMenu = () => {
    setActiveNode(null);
    setMessages((m) => [...m, { from: "bot", text: "Que veux-tu faire d'autre ?" }]);
  };

  if (!user) return null;

  return (
    <>
      <style>{`
        .moga-chat-fab {
          position: fixed; right: 24px; bottom: 24px; z-index: 1080;
          width: 60px; height: 60px; border-radius: 50%; border: none;
          background: linear-gradient(135deg, #f97316, #ea580c);
          box-shadow: 0 8px 24px rgba(249,115,22,.45);
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; transition: transform .15s ease, opacity .15s ease;
        }
        .moga-chat-fab:hover { transform: scale(1.07); }
        .moga-chat-fab img { width: 34px; height: 34px; object-fit: contain; }
        @media (max-width: 767.98px) {
          .moga-chat-fab {
            width: 48px; height: 48px;
            right: 12px; bottom: 12px;
            opacity: .85;
          }
          .moga-chat-fab img { width: 26px; height: 26px; }
        }
        .moga-chat-panel {
          position: fixed; right: 24px; bottom: 96px; z-index: 1080;
          width: 360px; max-width: calc(100vw - 32px);
          height: 520px; max-height: calc(100vh - 140px);
          background: #fff; border-radius: 16px; overflow: hidden;
          box-shadow: 0 16px 48px rgba(0,0,0,.22);
          display: flex; flex-direction: column;
          animation: mogaChatIn .18s ease;
        }
        @keyframes mogaChatIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        .moga-chat-header {
          background: linear-gradient(135deg, #f97316, #ea580c); color: #fff;
          padding: 14px 16px; display: flex; align-items: center; gap: 10px;
        }
        .moga-chat-header .avatar {
          width: 36px; height: 36px; border-radius: 50%; background: #fff;
          display: flex; align-items: center; justify-content: center;
        }
        .moga-chat-header .avatar img { width: 24px; height: 24px; object-fit: contain; }
        .moga-chat-header .title { font-weight: 700; line-height: 1.1; }
        .moga-chat-header .status { font-size: 11px; opacity: .9; }
        .moga-chat-close { margin-left: auto; background: transparent; border: none; color: #fff; cursor: pointer; }
        .moga-chat-body { flex: 1; overflow-y: auto; padding: 14px; background: #f8fafc; }
        .moga-msg { display: flex; margin-bottom: 10px; }
        .moga-msg.bot { justify-content: flex-start; }
        .moga-msg.user { justify-content: flex-end; }
        .moga-bubble {
          max-width: 80%; padding: 9px 12px; border-radius: 14px; font-size: 14px; line-height: 1.4;
        }
        .moga-msg.bot .moga-bubble { background: #fff; color: #1e293b; border: 1px solid #e2e8f0; border-bottom-left-radius: 4px; }
        .moga-msg.user .moga-bubble { background: #f97316; color: #fff; border-bottom-right-radius: 4px; }
        .moga-chat-quick { padding: 10px 12px; border-top: 1px solid #e2e8f0; background: #fff; display: flex; flex-wrap: wrap; gap: 6px; }
        .moga-quick-btn {
          border: 1px solid #f97316; color: #ea580c; background: #fff;
          border-radius: 999px; padding: 6px 12px; font-size: 13px; cursor: pointer; transition: all .12s ease;
        }
        .moga-quick-btn:hover { background: #f97316; color: #fff; }
        .moga-link-btn {
          border: none; background: #1e293b; color: #fff; border-radius: 8px;
          padding: 6px 12px; font-size: 13px; cursor: pointer; margin-top: 6px;
          display: inline-flex; align-items: center; gap: 6px;
        }
        .moga-wa-btn {
          width: 100%; border: none; background: #25D366; color: #fff;
          border-radius: 999px; padding: 8px 12px; font-size: 13px; font-weight: 600;
          cursor: pointer; display: inline-flex; align-items: center; justify-content: center;
          gap: 6px; transition: background .12s ease;
        }
        .moga-wa-btn:hover { background: #1ebe5b; }
      `}</style>

      {open && (
        <div className="moga-chat-panel">
          <div className="moga-chat-header">
            <div className="avatar">
              <img src={mogaLogo} alt="Moga" />
            </div>
            <div>
              <div className="title">Moga</div>
              <div className="status">● Assistant en ligne</div>
            </div>
            <button
              className="moga-chat-close"
              onClick={() => setOpen(false)}
              aria-label="Fermer"
            >
              <FeatherIcon icon="x" size={20} />
            </button>
          </div>

          <div className="moga-chat-body" ref={bodyRef}>
            {messages.map((m, i) => (
              <div key={i} className={`moga-msg ${m.from}`}>
                <div className="moga-bubble">
                  {m.text}
                  {m.from === "bot" &&
                    activeNode &&
                    activeNode.link &&
                    i === messages.length - 1 && (
                      <div>
                        <button
                          className="moga-link-btn"
                          onClick={() => {
                            navigate(activeNode.link!.path);
                            setOpen(false);
                          }}
                        >
                          <FeatherIcon icon="arrow-right" size={14} />
                          {activeNode.link.label}
                        </button>
                      </div>
                    )}
                </div>
              </div>
            ))}
          </div>

          <div className="moga-chat-quick">
            {WHATSAPP_URL && (
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="moga-wa-btn"
              >
                <FeatherIcon icon="message-square" size={15} />
                Discuter sur WhatsApp
              </a>
            )}
            {activeNode && (
              <button className="moga-quick-btn" onClick={backToMenu}>
                ← Menu
              </button>
            )}
            {topics.map((node) => (
              <button
                key={node.id}
                className="moga-quick-btn"
                onClick={() => pickTopic(node)}
              >
                {node.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        className="moga-chat-fab"
        onClick={() => setOpen((o) => !o)}
        aria-label="Ouvrir le chat Moga"
      >
        {open ? (
          <FeatherIcon icon="x" size={26} color="#fff" />
        ) : (
          <FeatherIcon icon="message-circle" size={28} color="#fff" />
        )}
      </button>
    </>
  );
};

export default MogaChatbot;
